import { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useMouseStore } from '../../hooks/stores/mouseStore';

export const FloatingPanel = ({
  children,
  x,
  y,
  label,
  style,
  onDrag,
  onClose,
}: {
  children?: ReactNode;
  x: number;
  y: number;
  label?: string;
  style?: React.CSSProperties;
  onDrag?: (x: number, y: number) => void;
  onClose?: () => void;
}) => {
  const [mouseData] = useMouseStore();
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x, y });

  useEffect(() => {
    if (mouseData.isDown === false) {
      setIsDragging(false);
    }
  }, [mouseData.isDown]);

  useEffect(() => {
    if (
      isDragging &&
      onDrag &&
      mouseData.draggingDiff &&
      mouseData.draggingStartPos
    ) {
      onDrag(
        mouseData.draggingDiff.x + startPos.x,
        mouseData.draggingDiff.y + startPos.y
      );
    }
  }, [isDragging, mouseData.draggingDiff]);

  return (
    <Container
      $isDragging={isDragging}
      style={{ ...(style || {}), left: x, top: y }}
    >
      <TitleBar
        onMouseDown={() => {
          setIsDragging(true);
          setStartPos({ x, y });
        }}
      >
        {label}
        {onClose && (
          <CloseButton onClick={onClose}>
            <span className="icon-x" />
          </CloseButton>
        )}
      </TitleBar>
      <Content className="scroll">
        {children}
      </Content>
    </Container>
  );
};

const TitleBar = styled.div`
  cursor: grab;
  box-sizing: border-box;
  width: 100%;
  padding: 5px;
  position: sticky;
  top: 0;
  z-index: 1;

  display: flex;
  align-items: center;
  justify-content: space-between;

  background-color: var(--neutral-3);
  border-radius: var(--radius-2) var(--radius-2) 0 0;

  font-family: var(--font-1);
  color: var(--main-2);
  font-size: 13px;

  &:active {
    cursor: grabbing;
  }
`;

const Container = styled.div<{ $isDragging: boolean }>`
  box-sizing: border-box;
  position: absolute;
  z-index: 2;
  transition: opacity var(--transition-2);
  max-height: calc(100vh - 240px);
  overflow: hidden;

  background-color: var(--neutral-2);
  border: 1px solid var(--neutral-3);
  border-radius: var(--radius-2);

  box-shadow: var(--shadow-1);

  ${({ $isDragging }) =>
    $isDragging &&
    `
    opacity: 0.5;
  `};
`;

const Content = styled.div`
  box-sizing: border-box;
  max-height: calc(100vh - 270px);
  overflow-y: auto;
`;

const CloseButton = styled.div`
  cursor: pointer;
  color: var(--main-2);
  transition: color var(--transition-1);

  &:hover {
    color: var(--main-1);
  }
`;
